import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Pill } from '../components/ui/Pill'
import { Divider } from '../components/ui/Divider'
import { Field } from '../components/ui/Field'
import { ChannelDot } from '../components/ui/Channel'
import { Icon, I } from '../components/ui/icons'
import { TlaaqLogo } from '../components/brand/TlaaqLogo'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { gridColumns, mobileButtonStyle } from '../components/ui'

const STEPS = ['createAccount', 'connectStore', 'sourceOfTruth', 'youreSet'] as const

function Stepper({ step }: { step: number }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  return (
    <div style={{ padding: isMobile ? '24px 14px 12px' : '32px 28px 12px', display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, minWidth: 0 }}>
        {STEPS.map((key, i) => {
          const num = i + 1
          const done = num < step
          const active = num === step
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: done ? 'var(--success)' : active ? 'var(--brand-500)' : 'var(--ink-100)',
                  color: done || active ? 'white' : 'var(--fg-subtle)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {done ? <Icon size={13}>{I.check}</Icon> : num}
                </div>
                <span style={{ fontWeight: active ? 700 : 500, color: active ? 'var(--brand-900)' : 'var(--fg-muted)' }}>{t(`pages.onboarding.steps.${key}`)}</span>
              </div>
              {i < STEPS.length - 1 && <div style={{ width: 36, height: 1, background: 'var(--border-default)' }} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Signup({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation()
  return (
    <Card padding={36} style={{ width: 460, maxWidth: '100%' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', margin: 0 }}>{t('pages.onboarding.signup.title')}</h2>
        <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 6, marginBottom: 0 }}>{t('pages.onboarding.signup.subtitle')}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label={t('pages.onboarding.signup.fullName')} placeholder={t('pages.onboarding.signup.fullNamePlaceholder')} />
        <Field label={t('pages.onboarding.signup.email')} placeholder={t('pages.onboarding.signup.emailPlaceholder')} />
        <Field label={t('pages.onboarding.signup.storeName')} placeholder={t('pages.onboarding.signup.storeNamePlaceholder')} hint={t('pages.onboarding.signup.storeNameHint')} />
        <Field label={t('pages.onboarding.signup.password')} type="password" placeholder="••••••••" />
        <Button kind="primary" size="lg" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={onNext}>
          {t('pages.onboarding.signup.createAccount')}
        </Button>
      </div>
      <Divider style={{ margin: '22px 0' }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <Button kind="secondary" size="md" style={{ flex: 1, justifyContent: 'center' }}>{t('pages.onboarding.signup.continueGoogle')}</Button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', textAlign: 'center', marginTop: 18 }}>
        {t('pages.onboarding.signup.terms')}
      </div>
    </Card>
  )
}

function ConnectStore({ onNext }: { onNext: (channelName: string) => void }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const channels = [
    { name: 'Salla', descKey: 'pages.onboarding.channels.salla', featured: true },
    { name: 'Noon', descKey: 'pages.onboarding.channels.noon', featured: true },
    { name: 'Amazon', descKey: 'pages.onboarding.channels.amazon' },
    { name: 'Zid', descKey: 'pages.onboarding.channels.zid' },
    { name: 'Trendyol', descKey: 'pages.onboarding.channels.trendyol' },
  ]
  return (
    <div style={{ width: 720, maxWidth: '100%' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', margin: 0 }}>{t('pages.onboarding.connect.title')}</h2>
        <p style={{ fontSize: 14.5, color: 'var(--fg-muted)', marginTop: 8, marginBottom: 0 }}>
          {t('pages.onboarding.connect.subtitle')}
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 12 }}>
        {channels.map(c => (
          <Card key={c.name} padding={18} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 14 }}>
            <ChannelDot name={c.name} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-900)' }}>{c.name}</div>
                {c.featured && <Pill tone="brand" size="sm">{t('pages.onboarding.connect.popular')}</Pill>}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', marginTop: 2 }}>{t(c.descKey)}</div>
            </div>
            <Button kind="secondary" size="sm" icon={I.link} style={mobileButtonStyle(isMobile)} onClick={() => onNext(c.name)}>{t('pages.onboarding.connect.connect')}</Button>
          </Card>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 12, marginTop: 24 }}>
        <button style={{ background: 'transparent', border: 'none', color: 'var(--fg-subtle)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('pages.onboarding.connect.skip')}
        </button>
        <Button kind="primary" size="md" icon={I.arrowR} style={mobileButtonStyle(isMobile)} onClick={() => onNext('Zid')}>{t('pages.onboarding.common.continue')}</Button>
      </div>
    </div>
  )
}

function SourceOfTruth({ onNext, onBack, channelName }: { onNext: () => void; onBack: () => void; channelName: string }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [pick, setPick] = useState<'tlaaq' | 'channel' | 'skip'>('channel')
  const [pullNow, setPullNow] = useState(true)
  const options = [
    {
      id: 'channel' as const,
      titleKey: 'pages.onboarding.source.options.channel.title',
      subKey: 'pages.onboarding.source.options.channel.sub',
      bodyKey: 'pages.onboarding.source.options.channel.body',
      recommended: true,
    },
    {
      id: 'tlaaq' as const,
      titleKey: 'pages.onboarding.source.options.tlaaq.title',
      subKey: 'pages.onboarding.source.options.tlaaq.sub',
      bodyKey: 'pages.onboarding.source.options.tlaaq.body',
    },
    {
      id: 'skip' as const,
      titleKey: 'pages.onboarding.source.options.skip.title',
      subKey: 'pages.onboarding.source.options.skip.sub',
      bodyKey: 'pages.onboarding.source.options.skip.body',
    },
  ]

  return (
    <Card padding={isMobile ? 24 : 36} style={{ width: 760, maxWidth: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <Pill tone="brand" size="sm">{t('pages.onboarding.source.almostDone')}</Pill>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', margin: '10px 0 8px' }}>
          {t('pages.onboarding.source.title')}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--fg-muted)', margin: 0, lineHeight: 1.55, maxWidth: 620 }}>
          {t('pages.onboarding.source.subtitle', { channel: channelName })}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(o => {
          const sel = pick === o.id
          return (
            <button key={o.id} onClick={() => setPick(o.id)} style={{
              display: 'grid', gridTemplateColumns: isMobile ? '44px minmax(0, 1fr)' : '44px 1fr auto', gap: 14, alignItems: 'center',
              padding: '16px 18px', borderRadius: 12, textAlign: 'start', cursor: 'pointer',
              background: sel ? 'var(--brand-100)' : 'white',
              border: sel ? '1.5px solid var(--brand-500)' : '1px solid var(--border-subtle)',
              fontFamily: 'inherit', position: 'relative',
            }}>
              {o.recommended && (
                <div style={{ position: 'absolute', top: -9, insetInlineEnd: 16, padding: '3px 9px', background: 'var(--brand-500)', color: 'white', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {t('pages.onboarding.source.recommended')}
                </div>
              )}
              {o.id === 'channel' ? (
                <ChannelDot name={channelName} size={40} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 10, background: o.id === 'tlaaq' ? 'var(--grad-brand)' : 'var(--ink-300)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: 18 }}>
                  {o.id === 'tlaaq' ? 'T' : '…'}
                </div>
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--brand-900)' }}>{t(o.titleKey, { channel: channelName })}</span>
                  <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>· {t(o.subKey)}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.5 }}>{t(o.bodyKey, { channel: channelName })}</div>
              </div>
              <div style={{ width: 22, height: 22, borderRadius: '50%', border: sel ? '6px solid var(--brand-500)' : '2px solid var(--border-default)', background: 'white', flexShrink: 0, gridColumn: isMobile ? '1 / -1' : undefined, justifySelf: isMobile ? 'end' : undefined }} />
            </button>
          )
        })}
      </div>

      {pick !== 'skip' && (
        <div style={{ marginTop: 16, padding: 16, borderRadius: 14, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 4 }}>
            {t('pages.onboarding.source.pull.title')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5, marginBottom: 12 }}>
            {pick === 'channel'
              ? t('pages.onboarding.source.pull.channelBody', { channel: channelName })
              : t('pages.onboarding.source.pull.tlaaqBody', { channel: channelName })}
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, flexWrap: 'wrap' }}>
            <Button kind={pullNow ? 'primary' : 'secondary'} size="sm" style={mobileButtonStyle(isMobile)} onClick={() => setPullNow(true)}>{t('pages.onboarding.source.pull.now')}</Button>
            <Button kind={!pullNow ? 'primary' : 'secondary'} size="sm" style={mobileButtonStyle(isMobile)} onClick={() => setPullNow(false)}>{t('pages.onboarding.source.pull.later')}</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 12, marginTop: 24 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: 'var(--fg-subtle)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>{t('pages.onboarding.common.back')}</button>
        <Button kind="primary" size="lg" icon={I.arrowR} style={mobileButtonStyle(isMobile)} onClick={onNext}>{t('pages.onboarding.common.continue')}</Button>
      </div>
    </Card>
  )
}

function Welcome({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  return (
    <Card padding={44} style={{ width: 520, maxWidth: '100%', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--brand-100)', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
        <Icon size={32}>{I.check}</Icon>
      </div>
      <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em', margin: 0 }}>{t('pages.onboarding.welcome.title')}</h2>
      <p style={{ fontSize: 14.5, color: 'var(--fg-muted)', marginTop: 10, marginBottom: 24 }}>
        {t('pages.onboarding.welcome.body')}
      </p>
      <Button kind="primary" size="lg" icon={I.arrowR} style={mobileButtonStyle(isMobile)} onClick={onDone}>{t('pages.onboarding.welcome.dashboard')}</Button>
    </Card>
  )
}

interface OnboardingPageProps {
  onComplete?: () => void
}

export function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState(1)
  const [connectedChannel, setConnectedChannel] = useState('Zid')
  const isMobile = useIsMobileViewport()

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: isMobile ? '18px 16px' : '20px 32px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white' }}>
        <TlaaqLogo height={26} />
        <button style={{ fontSize: 13, color: 'var(--fg-subtle)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
          {t('pages.onboarding.signOut')}
        </button>
      </header>

      <Stepper step={step} />

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '18px 14px 48px' : '20px 28px 60px' }}>
        {step === 1 && <Signup onNext={() => setStep(2)} />}
        {step === 2 && <ConnectStore onNext={(channelName) => { setConnectedChannel(channelName); setStep(3) }} />}
        {step === 3 && <SourceOfTruth channelName={connectedChannel} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
        {step === 4 && <Welcome onDone={onComplete ?? (() => {})} />}
      </main>
    </div>
  )
}
